import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ClipboardList,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Pause,
  HelpCircle,
  Filter,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-500/30', icon: Clock },
  in_review: { label: 'In Review', color: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/30', icon: XCircle },
  on_hold: { label: 'On Hold', color: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-500/30', icon: Pause },
  needs_info: { label: 'Needs Info', color: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-500/30', icon: HelpCircle },
};

const priorityConfig = {
  high: { label: 'High', color: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400' },
  medium: { label: 'Medium', color: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-400' },
  low: { label: 'Low', color: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400' },
};

export default function MyTasksPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/cogs-cards/my-tasks`);
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/cogs-cards/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (statusFilter !== 'all' && task.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && task.priority !== priorityFilter) return false;
    return true;
  });

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <MainLayout>
      <div className="p-8 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">My Tasks</h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1">COGS cards assigned to you for review</p>
          </div>
          {stats && (
            <div className="flex gap-4">
              <div className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-500/20 rounded-lg px-4 py-2 text-center">
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.my_pending}</p>
                <p className="text-xs text-yellow-600/70 dark:text-yellow-400/70">Pending Review</p>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-40 bg-white dark:bg-zinc-900 border-zinc-300 dark:border-zinc-700">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                <SelectItem value="all">All Priorities</SelectItem>
                {Object.entries(priorityConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-zinc-500 ml-auto">
              {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Tasks List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full" />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-12 text-center">
            <ClipboardList className="w-12 h-12 text-zinc-400 dark:text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-600 dark:text-zinc-400">No tasks found</h3>
            <p className="text-zinc-500 mt-1">
              {tasks.length === 0 ? "You don't have any assigned tasks" : "No tasks match your filters"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map(task => {
              const StatusIcon = statusConfig[task.status]?.icon || Clock;
              const overdue = isOverdue(task.due_date);
              
              return (
                <div
                  key={task.id}
                  onClick={() => navigate(`/cogs-cards/${task.id}`)}
                  className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 hover:border-zinc-300 dark:hover:border-zinc-700 cursor-pointer transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${statusConfig[task.status]?.color || 'bg-zinc-100 dark:bg-zinc-800'}`}>
                        <StatusIcon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-zinc-900 dark:text-white">{task.part_number}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${priorityConfig[task.priority]?.color || 'bg-zinc-100 dark:bg-zinc-800'}`}>
                            {priorityConfig[task.priority]?.label || task.priority}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">{task.part_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-right">
                      <div>
                        <p className="text-lg font-semibold text-zinc-900 dark:text-white">
                          {task.currency} {task.dealer_price?.toLocaleString()}
                        </p>
                        <p className="text-xs text-zinc-500">Dealer Price</p>
                      </div>
                      {task.due_date && (
                        <div className={`flex items-center gap-1 ${overdue ? 'text-red-500 dark:text-red-400' : 'text-zinc-500 dark:text-zinc-400'}`}>
                          <Calendar className="w-4 h-4" />
                          <span className="text-sm">
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      <span className={`px-3 py-1 rounded-full text-xs border ${statusConfig[task.status]?.color || 'bg-zinc-100 dark:bg-zinc-800'}`}>
                        {statusConfig[task.status]?.label || task.status}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
